export enum ThemeType {
  CUSTOM = 'custom',
  NAMED = 'named',
}

export const themeColorNames = [
  'surface',
  'on-surface',
  'primary',
  'on-primary',
  'voting',
  'on-voting',
] as const;

export type ThemeColors = {
  [key in (typeof themeColorNames)[number]]?: string;
};

export type CustomTheme = ThemeColors & {
  type: ThemeType.CUSTOM;
};

export const ThemeNames = [
  'opinionx-default',
  'professional-blue',
  'sunset-coral',
  'minty-fresh',
  'rosewood-blush',
  'dark-mode',
] as const;

export type ThemeName = (typeof ThemeNames)[number];

export type NamedTheme = {
  type: ThemeType.NAMED;
  name: ThemeName;
};

export type SurveyTheme = CustomTheme | NamedTheme;

type NamedThemes = {
  [key in Exclude<keyof typeof ThemeNames, keyof []>]: {
    colors: Required<ThemeColors>;
    name: (typeof ThemeNames)[key];
    displayName: string;
  };
};

export const namedThemes = [
  {
    name: 'opinionx-default',
    displayName: 'OpinionX Default',
    colors: {
      surface: '#FFFFFF',
      'on-surface': '#000000',
      primary: '#0F1F27',
      'on-primary': '#FFFFFF',
      voting: '#E0E0E0',
      'on-voting': '#000000',
    },
  },
  {
    name: 'professional-blue',
    displayName: 'Professional Blue',
    colors: {
      surface: '#F8FAFC',
      'on-surface': '#0F172A',
      primary: '#1E3A8A',
      'on-primary': '#FFFFFF',
      voting: '#DBEAFE',
      'on-voting': '#1E3A8A',
    },
  },
  {
    name: 'sunset-coral',
    displayName: 'Sunset Coral',
    colors: {
      surface: '#FFFBF5',
      'on-surface': '#3B0A00',
      primary: '#FF6B35',
      'on-primary': '#FFFFFF',
      voting: '#FFD6C2',
      'on-voting': '#3B0A00',
    },
  },
  {
    name: 'minty-fresh',
    displayName: 'Minty Fresh',
    colors: {
      surface: '#F0FFF4',
      'on-surface': '#064E3B',
      primary: '#10B981',
      'on-primary': '#FFFFFF',
      voting: '#D1FAE5',
      'on-voting': '#064E3B',
    },
  },
  {
    name: 'rosewood-blush',
    displayName: 'Rosewood Blush',
    colors: {
      surface: '#FAEEF1',
      'on-surface': '#BF395D',
      primary: '#C75875',
      'on-primary': '#FFFFFF',
      voting: '#E4ADBC',
      'on-voting': '#5B1129',
    },
  },
  {
    name: 'dark-mode',
    displayName: 'Dark Mode',
    colors: {
      surface: '#121212',
      'on-surface': '#E5E5E7',
      primary: '#4D8B9E',
      'on-primary': '#FFFFFF',
      voting: '#3A3A3E',
      'on-voting': '#F1F1F3',
    },
  },
] as const satisfies NamedThemes;

const namedThemeColors = Object.fromEntries(
  namedThemes.map((theme) => [theme.name, theme.colors]),
) as Record<ThemeName, Required<ThemeColors>>;

export const getNamedThemeColors = (themeName: ThemeName): ThemeColors =>
  namedThemeColors[themeName];
